import { type FormEvent, useEffect, useRef, useState } from "react";

import { api } from "@/lib/api";

interface Answer {
  thread_id: string | null;
  answer: string;
  model: string;
  tools_used: string[];
}

/**
 * Quick-ask from anywhere.
 *
 * Deliberately separate from the advice flow: advice interprets one reading and
 * is saved against it, this is for whatever you happen to be wondering.
 */
export default function AskModal({ onClose }: { onClose: () => void }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setAnswer(await api.post<Answer>("/api/chat/ask", { question, persist: true }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not ask right now.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Ask about the pool"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-(--color-surface-raised) p-5 sm:rounded-2xl">
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Ask about the pool</h2>
          <button type="button" onClick={onClose} className="text-sm text-(--color-ink-muted)">
            Close
          </button>
        </div>

        <form onSubmit={submit} className="mt-3">
          <textarea
            ref={inputRef}
            rows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Why does my chlorine keep dropping?"
            className="field resize-y"
          />
          <button
            type="submit"
            disabled={busy || question.trim() === ""}
            className="btn btn-primary mt-3 w-full"
          >
            {busy ? "Thinking…" : "Ask"}
          </button>
        </form>

        {error && (
          <p role="alert" className="mt-3 text-sm text-(--color-status-high)">
            {error}
          </p>
        )}

        {answer && (
          <div className="mt-4">
            <p className="whitespace-pre-wrap text-sm">{answer.answer}</p>
            {answer.tools_used.length > 0 && (
              <p className="mt-3 text-xs text-(--color-ink-faint)">
                Looked up: {[...new Set(answer.tools_used)].join(", ").replace(/_/g, " ")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
